import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { createYearlyPlan } from "../../actions/postAction";
import { decreasetokens, loginWithGoogle } from "../../actions/userAction";
import { FIVE_YEAR_CATEGORIES } from "./fiveYearCategories";
import "./VisionStartFlow.css";

const PENDING_KEY = "dmyor_vision_pending";
const TEXT_LIMIT = 200;
const MIN_CHARS = 10;

const ALL_EXAMPLES = [
  "Growing my YouTube channel to 100K subscribers",
  "I am going to make my business a success",
  "Exploring 10 new countries in the next 2 years",
  "Getting into the best shape of my life",
  "Writing and publishing my first book",
  "Building a SaaS that reaches $10K MRR",
  "Running a sub-4-hour marathon",
  "Leaving my job and going full-time on my own thing",
  "Learning a new language fluently",
  "Saving my first $100K",
  "Building an audience of 50K on Instagram",
  "Launching a t-shirt brand with real creativity",
  "Getting my finances in order and staying debt-free",
  "Moving to a city I actually want to live in",
  "Starting a podcast and hitting 10K listeners",
];

const DURATION_THEMES = {
  1: { label: "1 Year" },
  2: { label: "2 Years" },
  3: { label: "3 Years" },
  4: { label: "4 Years" },
  5: { label: "PROJECT-5" },
};



const deriveName = (text) => {
  const t = (text || "").trim();
  if (!t) return "Untitled Vision";
  return t.length <= 40 ? t : t.slice(0, 40).trim() + "…";
};

const VisionStartFlow = ({ guestMode = true }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated } = useSelector((state) => state.user || {});

  const [step, setStep] = useState(0);
  const [duration, setDuration] = useState(3);
  const [text, setText] = useState("");
  const [category, setCategory] = useState(null);
  const [otherInput, setOtherInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [p5Glitching, setP5Glitching] = useState(false);
  const prevDurationRef = useRef(3);

  useEffect(() => {
    if (duration === 5 && prevDurationRef.current !== 5) {
      setP5Glitching(true);
      const t = setTimeout(() => setP5Glitching(false), 520);
      prevDurationRef.current = 5;
      return () => clearTimeout(t);
    }
    prevDurationRef.current = duration;
  }, [duration]);

  useEffect(() => {
    // URL params take priority (set by Google OAuth redirect)
    const params = new URLSearchParams(location.search);
    const urlText = params.get('t');
    const urlCategory = params.get('c');
    const urlDuration = params.get('d');
    if (urlText) {
      setText(urlText);
      if (urlDuration) setDuration(Number(urlDuration));
      if (urlCategory) {
        setCategory(urlCategory);
        setStep(3);
      } else {
        setStep(2);
      }
      // Clean URL without reload
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    // Fallback to localStorage
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.text) {
        setText(parsed.text);
        if (parsed.duration) setDuration(parsed.duration);
        if (parsed.category) {
          setCategory(parsed.category);
          setStep(3);
        } else {
          setStep(2);
        }
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!text.trim()) {
      localStorage.removeItem(PENDING_KEY);
      return;
    }
    try {
      localStorage.setItem(
        PENDING_KEY,
        JSON.stringify({ text, duration })
      );
    } catch (_) {}
  }, [text, duration]);

  const continueDisabled = text.trim().length < MIN_CHARS;

  const handleContinueText = () => {
    if (continueDisabled) return;
    setCategory(null);
    setOtherInput("");
    setStep(3);
  };

  const handleConfirm = async (chosen) => {
    const cat = chosen !== undefined ? chosen : category;

    if (guestMode && !isAuthenticated) {
      try {
        localStorage.setItem(PENDING_KEY, JSON.stringify({ text, duration, category: cat || "" }));
      } catch (_) {}
      setStep(4);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: deriveName(text),
        heading: text.trim(),
        durationInYears: duration,
        category: cat || "",
      };
      const result = await dispatch(createYearlyPlan(payload));
      if (!result || !result.planId) {
        setSubmitting(false);
        if (result?.error && result?.status === 401) {
          try {
            localStorage.setItem(PENDING_KEY, JSON.stringify({ text, duration, category: cat || "" }));
          } catch (_) {}
          setStep(4);
        }
        return;
      }
      const { planId } = result;
      dispatch(decreasetokens(1));
      localStorage.removeItem(PENDING_KEY);
      try {
        localStorage.setItem(`dmyor_new_vision_${planId}`, "1");
      } catch (_) {}
      navigate("/dashboard/start/booting", {
        state: { planId, durationInYears: duration, category: cat || "" },
      });
    } catch (_) {
      toast.error("Could not create your Vision. Try again.");
      setSubmitting(false);
    }
  };

  const examples = useMemo(() => {
    const shuffled = [...ALL_EXAMPLES].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 4);
  }, []);

  const allCategories = Object.keys(FIVE_YEAR_CATEGORIES);
  const theme = DURATION_THEMES[duration];

  return (
    <div className={`vsf-page ${duration === 5 ? "vsf-theme-p5" : "vsf-theme-y1"} vsf-step-${step} ${p5Glitching ? "vsf-p5-glitching" : ""}`}>
      {p5Glitching && (
        <div className="vsf-glitch-fx" aria-hidden="true">
          <div className="vsf-glitch-scan" />
          <div className="vsf-glitch-bars" />
          <div className="vsf-glitch-flash" />
        </div>
      )}
      <Helmet>
        <title>Start your Vision — Dmyor</title>
      </Helmet>

      <div className="vsf-bg" aria-hidden="true">
        <div className="vsf-bg-orb vsf-bg-orb-a" />
        <div className="vsf-bg-orb vsf-bg-orb-b" />
        <div className="vsf-bg-grid" />
      </div>

      {step === 0 && (
        <div className="vsf-stage vsf-stage-intro">
          <p className="vsf-eyebrow">Welcome</p>
          <h1 className="vsf-heading">What's a Vision?</h1>
          <p className="vsf-subhead">
            Something you're going to build, on the record. Write it now. Document the journey. Release it when it's real.
          </p>

          <div className="vsf-actions vsf-actions-intro">
            <button
              type="button"
              className="vsf-hero-cta"
              onClick={() => setStep(1)}
            >
              <span className="vsf-hero-cta-text">Start building.</span>
              <span className="vsf-hero-cta-arrow" aria-hidden="true">→</span>
            </button>
            <p className="vsf-hero-cta-note">Takes a minute. You can edit anytime before release.</p>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="vsf-stage vsf-stage-duration">
          <p className="vsf-eyebrow">Step 1 of 3 · Horizon</p>
          <h1 className="vsf-heading">
            How far out are we looking?
          </h1>
          <p className="vsf-subhead">
            This is the time you are giving yourself to make your vision a reality.
          </p>

          <div className="vsf-duration-display">
            <div className="vsf-duration-ring">
              <span className="vsf-duration-num">
                {duration === 5 ? "P5" : duration}
              </span>
            </div>
            <span className="vsf-duration-label">{theme.label}</span>
          </div>

          {duration === 5 && (
            <Link
              to="/project-5"
              target="_blank"
              rel="noopener noreferrer"
              className="vsf-p5-link"
            >
              <span className="vsf-p5-link-q">?</span>
              <span>What is Project-5</span>
              <span className="vsf-p5-link-arrow">↗</span>
            </Link>
          )}

          <div className="vsf-year-row">
            {[1, 2, 3, 4, 5].map((y) => (
              <button
                key={y}
                type="button"
                className={`vsf-year-pill ${duration === y ? "active" : ""} ${
                  y === 5 ? "vsf-year-pill-p5" : ""
                }`}
                onClick={() => setDuration(y)}
              >
                <span className="vsf-year-pill-num">{y === 5 ? "P5" : y}</span>
                <span className="vsf-year-pill-text">
                  {y === 5 ? "Project-5" : y === 1 ? "year" : "years"}
                </span>
              </button>
            ))}
          </div>

          <div className="vsf-actions">
            <button
              type="button"
              className="vsf-cta"
              onClick={() => setStep(2)}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="vsf-stage">
          <p className="vsf-eyebrow">Step 2 of 3 · Your Vision</p>
          <h1 className="vsf-heading">What's in yout mind?</h1>
          <p className="vsf-subhead">
            One thing you're committing to over the next {duration === 5 ? "5 years (Project-5)" : duration === 1 ? "1 year" : `${duration} years`}. This becomes your Vision heading.
          </p>

          <div className="vsf-input-wrap">
            <div className="vsf-textarea-chrome">
              <textarea
                className="vsf-textarea"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, TEXT_LIMIT))}
                placeholder="e.g. I'm going to build a company.(Customize this for your vision)"
                autoFocus
                rows={4}
              />
              <div className="vsf-textarea-footer">
                <div className="vsf-char-bar">
                  <div
                    className="vsf-char-bar-fill"
                    style={{
                      width: `${Math.min(100, (text.length / TEXT_LIMIT) * 100)}%`,
                      background: text.length > TEXT_LIMIT * 0.9 ? "#e0416d" : "#f17501",
                    }}
                  />
                </div>
                <span className={`vsf-counter-inline ${text.length > TEXT_LIMIT * 0.9 ? "vsf-counter-warn" : ""}`}>
                  {text.length}/{TEXT_LIMIT}
                </span>
              </div>
            </div>
          </div>

          <div className="vsf-examples">
            <p className="vsf-examples-label">Start with a popular vision</p>
            <div className="vsf-chip-row">
              {examples.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  className="vsf-chip"
                  onClick={() => setText(ex)}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>

          <div className="vsf-actions vsf-actions-row">
            <button type="button" className="vsf-secondary" onClick={() => setStep(1)}>Back</button>
            <button type="button" className="vsf-cta" onClick={handleContinueText} disabled={continueDisabled}>
              Continue →
            </button>
          </div>
          {continueDisabled && text.length > 0 && (
            <p className="vsf-hint-min">
              {MIN_CHARS - text.trim().length} more character{MIN_CHARS - text.trim().length === 1 ? "" : "s"} to continue
            </p>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="vsf-stage vsf-stage-auth">
          <p className="vsf-eyebrow">One last step</p>
          <h1 className="vsf-heading vsf-heading-auth">Your Vision is ready.</h1>
          <p className="vsf-subhead">
            Sign in and it gets created instantly. Your text is saved.
          </p>

          <div className="vsf-auth-preview">
            {category && <span className="vsf-cat-tag">{category}</span>}
            <p className="vsf-auth-preview-text">{text}</p>
          </div>

          <p className="vsf-auth-perks">
            Once inside, add records — specific things you are doing to make this real. Use AI Assist to figure out where to start.
          </p>

          <div className="vsf-auth-actions">
            <button
              type="button"
              className="vsf-btn-google"
              onClick={() => {
                const params = new URLSearchParams();
                if (text) params.set('t', text);
                if (category) params.set('c', category);
                params.set('d', duration);
                dispatch(loginWithGoogle(`/dashboard/start?${params.toString()}`));
              }}
            >
              <svg className="vsf-google-svg" viewBox="0 0 24 24" width="18" height="18">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="vsf-auth-divider">
              <span>or</span>
            </div>

            <Link to="/signup?next=/dashboard/start" className="vsf-auth-cta">
              Create Account
            </Link>
            <Link to="/login?next=/dashboard/start" className="vsf-auth-signin-link">
              Already have an account? Sign in
            </Link>
          </div>

          <button
            type="button"
            className="vsf-back-link"
            onClick={() => setStep(3)}
          >
            ← Back
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="vsf-stage">
          <p className="vsf-eyebrow">Step 3 of 3 · Tag (optional)</p>

          <div className="vsf-quote-card">
            <p className="vsf-quote-text">{text}</p>
          </div>

          <p className="vsf-classify-line">Tag your Vision (optional). You can always change it later.</p>

          <div className="vsf-cat-grid">
            {allCategories.map((cat) => {
              const isActive = category === cat && !otherInput;
              return (
                <button
                  key={cat}
                  type="button"
                  className={`vsf-cat-btn ${isActive ? "active" : ""}`}
                  onClick={() => {
                    if (isActive) {
                      setCategory(null);
                    } else {
                      setCategory(cat);
                      setOtherInput("");
                    }
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div className="vsf-other-row">
            <input
              type="text"
              className="vsf-other-input"
              placeholder="Or type your own (e.g. Travel, Spirituality)"
              value={otherInput}
              maxLength={40}
              onChange={(e) => {
                setOtherInput(e.target.value);
                if (e.target.value.trim()) setCategory(e.target.value.trim());
                else setCategory(null);
              }}
            />
          </div>

          <div className="vsf-confirm-row">
            <button
              type="button"
              className="vsf-cta"
              onClick={() => handleConfirm()}
              disabled={submitting}
            >
              {submitting ? "Creating..." : "Create Vision"}
            </button>
            <button
              type="button"
              className="vsf-secondary"
              onClick={() => {
                setStep(2);
                setOtherInput("");
                setCategory(null);
              }}
              disabled={submitting}
            >
              Back
            </button>
          </div>
          <p className="vsf-hint-min">No tag? Skip it. You can add records to your Vision next.</p>
        </div>
      )}
    </div>
  );
};

export default VisionStartFlow;
